import { ActivityItem, ActivityReport } from '@app/api/activity-report/models/activity-report.models';
import { ActivityPanelRange, ActivityPanelStateService } from './activity-panel-state.service';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnDestroy, OnInit, output, signal } from '@angular/core';
import { finalize, Observable, of, Subscription } from 'rxjs';
import {
    formatActivityTimestamp,
    getActivityStatusFilterKey,
    getActivityStatusIcon,
    getActivityTitle,
    hasMissingControllerDetails,
    mergeControllerDetails,
    resolveActivityStatus,
} from '@app/api/activity-report/activity-display';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AccountController } from '@app/api/controller/models/account-controller.model';
import { ActivityMonitorIconComponent } from '@app/shared/icon-svg/activity-monitor-icons/activity-monitor-icon.component';
import { ActivityReportManagerService } from '@app/api/activity-report/activity-report-manager.service';
import { BroadcastService } from '@app/common/services/broadcast.service';
import { Constants } from '@app/shared/models/icentral-constants.model';
import { ControllerManagerService } from '@app/api/controller/controller-manager.service';
import { ErrorHandlerService } from '@app/core/services';
import { IconSvgComponent } from '@app/shared/icon-svg/icon-svg.component';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WaitingIndicatorComponent } from '@app/shared/layout/header/waiting-indicator/waiting-indicator.component';

// Commented out, not deleted (AB#20613): the pre-Figma panel's Delete All / Mark All Read / Load New Activities and
// the per-card Mark Read / Delete used these. Restore together with the markup in the template.
// import { ActivityManagerService } from '@app/api/activities/activity-manager.service';
// import { CentralActivity } from '@app/api/activities/models/central-activity.model';
// import { UpdateActivityRequest } from '@app/api/activities/models/update-activity-request.model';
// import { ActivityCardComponent } from './activity-card/activity-card.component';

/** What one row of the panel shows. */
interface ActivityPanelRow {
    key: string;
    controllerName: string;
    timestamp: string;
    iconName: string;
    title: string;
    /** Details line, for rows without a change list. */
    message: string;
    /** "property: newValue", one per line; empty when the activity has no change list. */
    changes: string[];
}

@Component({
    host: { class: 'toro-notification-panel' },
    selector: 'ic-activity-monitor',
    templateUrl: './activity-monitor.component.html',
    styleUrls: ['./activity-monitor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ActivityMonitorIconComponent,
        IconSvgComponent,
        TranslatePipe,
        WaitingIndicatorComponent
    ]
})
export class ActivityMonitorComponent implements OnInit, OnDestroy {
    /** Changes shown before "+N more". */
    static readonly VISIBLE_CHANGE_COUNT = 3;

    private static readonly NO_ACTIVITY_MESSAGE: Record<ActivityPanelRange, string> = {
        today: 'STRINGS.NO_ACTIVITY_TODAY',
        yesterday: 'STRINGS.NO_ACTIVITY_YESTERDAY',
        last7Days: 'STRINGS.NO_ACTIVITY_LAST_7_DAYS',
    };

    private static readonly END_OF_RANGE_MESSAGE: Record<ActivityPanelRange, string> = {
        today: 'STRINGS.NO_MORE_ACTIVITY_TODAY',
        yesterday: 'STRINGS.NO_MORE_ACTIVITY_YESTERDAY',
        last7Days: 'STRINGS.NO_MORE_ACTIVITY_LAST_7_DAYS',
    };

    private readonly destroyRef = inject(DestroyRef);

    private activityReportManager = inject(ActivityReportManagerService);
    private broadcastService = inject(BroadcastService);
    private controllerManager = inject(ControllerManagerService);
    private errorHandler = inject(ErrorHandlerService);
    private panelState = inject(ActivityPanelStateService);
    private router = inject(Router);
    private ts = inject(TranslateService);

    /** Asks the toolbar to close the panel, e.g. after SEE MORE ACTIVITY navigates away. */
    readonly closePanel = output();

    protected readonly statusItems = Constants.ACTIVITY_STATUS_FILTER_ITEMS;
    protected readonly visibleChangeCount = ActivityMonitorComponent.VISIBLE_CHANGE_COUNT;
    protected readonly rangeOptions: ReadonlyArray<{ value: ActivityPanelRange; label: string }> = [
        { value: 'today', label: 'STRINGS.TODAY' },
        { value: 'yesterday', label: 'STRINGS.YESTERDAY' },
        { value: 'last7Days', label: 'STRINGS.LAST_7_DAYS' },
    ];

    // Signals: every value here is set from an API callback or a click (ADR 0008, AB#23266).
    protected readonly isLoading = signal(false);
    protected readonly isLoadingMore = signal(false);
    protected readonly activities = signal<ActivityItem[]>([]);
    protected readonly hasMore = signal(false);
    /** Rows whose full change list is showing ("+N more" clicked). */
    protected readonly expandedRows = signal<ReadonlySet<string>>(new Set());
    protected readonly showRangeMenu = signal(false);
    protected readonly showFilterMenu = signal(false);
    /**
     * Set after a Load More that brought rows but none the ticked statuses let through - without it the click
     * looks like it did nothing. Cleared by the next load or any filter change.
     */
    protected readonly noNewFilteredRows = signal(false);

    protected readonly range = this.panelState.range;
    protected readonly statuses = this.panelState.statuses;

    /** 'partial' puts the blue dot on FILTER; 'none' turns the menu's CLEAR ALL into SELECT ALL. */
    protected readonly filterSelection = computed<'all' | 'partial' | 'none'>(() => {
        const values = Object.values(this.statuses());
        const ticked = values.filter(Boolean).length;
        if (ticked === 0) { return 'none'; }
        return ticked === values.length ? 'all' : 'partial';
    });

    protected readonly rangeLabel = computed(() =>
        this.rangeOptions.find(option => option.value === this.range())?.label ?? 'STRINGS.LAST_7_DAYS');

    // The time range is applied by the API (loadWindow); only the status filter runs here, as on the report.
    protected readonly rows = computed<ActivityPanelRow[]>(() => {
        const statuses = this.statuses();
        const seen = new Map<string, number>();
        return this.activities()
            .filter(activity => this.matchesStatuses(activity, statuses))
            .map(activity => {
                // Keyed by what the activity is, not where it sits: new rows are prepended while the panel is open,
                // and a position-based key would move the "+N more" expanded state onto a different row.
                // The occurrence count keeps two identical payloads unique (a duplicate track key is NG0955).
                const identity = this.rowIdentity(activity);
                const occurrence = seen.get(identity) ?? 0;
                seen.set(identity, occurrence + 1);
                return this.toRow(activity, `${identity}#${occurrence}`);
            });
    });

    /**
     * Message when nothing is left to show: nothing in the range at all, the last Load More found nothing the
     * filters let through, or the filters hide everything loaded.
     */
    protected readonly emptyMessage = computed(() => {
        if (this.activities().length === 0) {
            return ActivityMonitorComponent.NO_ACTIVITY_MESSAGE[this.range()];
        }
        if (this.noNewFilteredRows()) {
            return this.hasMore() ? 'PARAGRAPHS.NO_NEW_ACTIVITY_FOR_FILTERS' : 'STRINGS.NO_MORE_ACTIVITY_FOR_FILTERS';
        }
        return 'STRINGS.NO_ACTIVITIES_FOUND';
    });

    /** Shown above SEE MORE ACTIVITY once the range has no more pages, so the end of the list is explicit. */
    protected readonly endOfRangeMessage = computed(() => ActivityMonitorComponent.END_OF_RANGE_MESSAGE[this.range()]);

    private accountId = '';
    private nextCursor: string | null = null;
    /** Fixed when the first page loads, so every Load More pages through the same window. */
    private loadWindow = ActivityPanelStateService.loadWindow();
    /** The first-page or Load More request in flight; a new first page cancels it. */
    private pageRequest?: Subscription;

    ngOnInit(): void {
        this.panelState.markOpened();

        this.broadcastService.selectedAccountChange
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((accountId: string) => !!accountId),
                filter((accountId: string) => accountId !== this.accountId),
            )
            .subscribe((accountId: string) => {
                this.accountId = accountId;
                this.loadFirstPage();
            });

        this.panelState.newActivityWhileOpen
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((newestMs: number) => this.onNewActivity(newestMs));
    }

    ngOnDestroy(): void {
        this.panelState.markClosed();
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    protected onToggleRangeMenu(event: Event): void {
        event.stopPropagation();
        this.showFilterMenu.set(false);
        this.showRangeMenu.update(open => !open);
    }

    protected onSelectRange(value: ActivityPanelRange): void {
        this.showRangeMenu.set(false);
        if (value === this.range()) { return; }

        this.panelState.setRange(value);
        this.loadFirstPage();
    }

    protected onToggleFilterMenu(event: Event): void {
        event.stopPropagation();
        this.showRangeMenu.set(false);
        this.showFilterMenu.update(open => !open);
    }

    protected onToggleStatus(key: string, checked: boolean): void {
        this.noNewFilteredRows.set(false);
        this.panelState.setStatus(key, checked);
    }

    protected onClearAllStatuses(): void {
        this.noNewFilteredRows.set(false);
        this.panelState.clearAllStatuses();
    }

    protected onSelectAllStatuses(): void {
        this.noNewFilteredRows.set(false);
        this.panelState.selectAllStatuses();
    }

    protected onToggleChanges(key: string): void {
        this.expandedRows.update(current => {
            const next = new Set(current);
            if (!next.delete(key)) { next.add(key); }
            return next;
        });
    }

    /** Clicks inside the panel but outside an open menu close that menu. */
    protected onPanelClick(): void {
        this.showRangeMenu.set(false);
        this.showFilterMenu.set(false);
    }

    protected onLoadMore(): void {
        if (!this.accountId || !this.hasMore() || this.isLoadingMore()) {
            return;
        }

        this.isLoadingMore.set(true);
        this.noNewFilteredRows.set(false);

        const { startDate, endDate } = this.loadWindow;
        this.pageRequest = this.loadPage(startDate, endDate, this.nextCursor)
            .pipe(
                finalize(() => this.isLoadingMore.set(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (report: ActivityReport) => {
                    const statuses = this.statuses();
                    const matched = report.activities.filter(activity => this.matchesStatuses(activity, statuses)).length;
                    this.noNewFilteredRows.set(report.activities.length > 0 && matched === 0);

                    this.activities.update(list => list.concat(report.activities));
                    this.setPaging(report);
                },
                error: err => { this.errorHandler.handleError(err); }
            });
    }

    protected onSeeMoreActivity(): void {
        this.router.navigate(['/report/activity-report']);
        this.closePanel.emit();
    }

    // protected onRefresh(): void {
    //     if (!this.accountId) {
    //         return;
    //     }
    //
    //     this.nextCursor = null;
    //     this.isLoadMoreDisabled.set(false);
    //     this.loadActivities();
    // }

    // protected onClosePanel(): void {
    //     this.closePanel.emit();
    // }

    // protected onDeleteAll(): void {
    //     if (!this.accountId) {
    //         return;
    //     }
    //
    //     this.activityManager.updateActivities(this.accountId, new UpdateActivityRequest({ isDeleted: true }))
    //         .subscribe({
    //             next: () => this.activities.set([]),
    //             error: err => { this.errorHandler.handleError(err); }
    //         });
    // }

    // protected onMarkAllRead(): void {
    //     if (!this.accountId) {
    //         return;
    //     }
    //
    //     this.activityManager.updateActivities(this.accountId, new UpdateActivityRequest({ isRead: true }))
    //         .subscribe({
    //             // New objects, so each card's activity input changes and it re-renders as read.
    //             next: () => this.activities.update(list => list.map(activity =>
    //                 Object.assign(Object.create(Object.getPrototypeOf(activity)), activity, { isRead: true }))),
    //             error: err => { this.errorHandler.handleError(err); }
    //         });
    // }

    // protected onActivityDeleted(activity: CentralActivity): void {
    //     this.activities.update(list => list.filter((a: CentralActivity) => a.id != activity.id));
    // }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private loadFirstPage(): void {
        // A range switched mid-request must not let the older answer land on top of the newer one.
        this.pageRequest?.unsubscribe();
        this.isLoadingMore.set(false);

        this.isLoading.set(true);
        this.noNewFilteredRows.set(false);
        this.nextCursor = null;
        this.loadWindow = ActivityPanelStateService.loadWindow(this.range());

        const { startDate, endDate } = this.loadWindow;
        this.pageRequest = this.loadPage(startDate, endDate)
            .pipe(
                finalize(() => this.isLoading.set(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (report: ActivityReport) => {
                    this.activities.set(report.activities);
                    this.setPaging(report);
                },
                error: err => {
                    this.activities.set([]);
                    this.hasMore.set(false);
                    this.errorHandler.handleError(err);
                }
            });
    }

    /** New activity arrived while the panel is open: put the rows newer than the current top at the top. */
    private onNewActivity(newestMs: number): void {
        const topMs = Date.parse(this.activities()[0]?.timestamp ?? '');
        // Yesterday's window is closed, so nothing new can belong in it.
        if (!this.accountId || this.range() === 'yesterday' || (Number.isFinite(topMs) && newestMs <= topMs)) {
            return;
        }

        const { startDate, endDate } = ActivityPanelStateService.loadWindow(this.range());
        this.loadPage(startDate, endDate)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (report: ActivityReport) => {
                    const newer = report.activities.filter(activity =>
                        !Number.isFinite(topMs) || Date.parse(activity.timestamp) > topMs);
                    this.activities.update(list => newer.concat(list));
                },
                error: err => { this.errorHandler.handleError(err); }
            });
    }

    /** One page of the chosen window, with controller names filled in the same way the report fills them. */
    private loadPage(startDate: string, endDate: string, cursor: string | null = null): Observable<ActivityReport> {
        return this.activityReportManager.getActivityPage(this.accountId, startDate, endDate, ActivityPanelStateService.PAGE_SIZE, cursor)
            .pipe(switchMap((report: ActivityReport) => {
                if (!hasMissingControllerDetails(report.activities)) {
                    return of(report);
                }

                return this.controllerManager.getAccountControllers().pipe(
                    map((accountControllers: AccountController[]) => {
                        mergeControllerDetails(report.activities, accountControllers);
                        return report;
                    }),
                    // Rows still show without names; the activity list matters more than the enrichment.
                    catchError(() => of(report)),
                );
            }));
    }

    private setPaging(report: ActivityReport): void {
        this.nextCursor = report.nextCursor;
        this.hasMore.set(report.nextCursor != null);
    }

    private rowIdentity(activity: ActivityItem): string {
        return [activity.transmissionId ?? '', activity.timestamp, activity.controller?.id ?? '', activity.category ?? '', activity.subCategory ?? '']
            .join('|');
    }

    /** Same rule as the report's Status filter: nothing ticked or everything ticked shows every row. */
    private matchesStatuses(activity: ActivityItem, statuses: Record<string, boolean>): boolean {
        const selected = Object.keys(statuses).filter(key => statuses[key]);
        if (selected.length === 0 || selected.length === Object.keys(statuses).length) {
            return true;
        }

        return selected.includes(getActivityStatusFilterKey(activity));
    }

    /**
     * Report mapping (activity-display.ts); the controller is on the row's top line, so it is not repeated below.
     * The report joins a change list with " · "; the panel puts each change on its own line, because a program
     * change easily carries 15+ of them.
     */
    private toRow(activity: ActivityItem, key: string): ActivityPanelRow {
        const changes = (activity.description?.changes ?? []).map(change => `${change.property}: ${change.newValue}`);

        return {
            key,
            controllerName: activity.controller?.name || '',
            timestamp: formatActivityTimestamp(activity.timestamp),
            iconName: getActivityStatusIcon(resolveActivityStatus(activity)),
            title: getActivityTitle(activity, this.ts),
            message: changes.length > 0 ? '' : activity.formattedDetails || '',
            changes,
        };
    }
}
